import { swNumberFilter } from "app/filters/ngFilters";

import { SEARCH_RESULTS_COUNT_FORMAT_MAX } from "../constants/search-results";

/**
 * Formats given number using `swNumberFilter()`
 * @param rawNumberOfResults
 * @example ```
 *     formatNumberOfResults(0) -> "0"
 *     formatNumberOfResults(12435) -> "12,435"
 *     formatNumberOfResults(100000) -> "100,000+"
 * ```
 */
export const formatNumberOfResults = (rawNumberOfResults: number): string => {
    if (rawNumberOfResults >= SEARCH_RESULTS_COUNT_FORMAT_MAX) {
        return `${swNumberFilter()(SEARCH_RESULTS_COUNT_FORMAT_MAX)}+`;
    }

    if (rawNumberOfResults === 0) {
        return String(rawNumberOfResults);
    }

    return swNumberFilter()(rawNumberOfResults);
};

/**
 * Compares given objects' `name` property ascending
 * @param a
 * @param b
 */
export function compareNamesAsc<T extends { name: string }>(a: T, b: T): number {
    return a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase());
}

/**
 * Checks whether given `string` includes given `searchTerm`
 * @param string
 * @param searchTerm
 */
export function stringMatchesSearchTerm(string: string, searchTerm: string) {
    return string.trim().toLowerCase().includes(searchTerm.trim().toLowerCase());
}

/**
 * Escapes special characters in given `string`
 * @param string
 */
export function escapeRegExpCharacters(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Retrieves a substring of given `string` that matches given `searchTerm`
 * @param searchTerm
 * @param string
 */
export function getMatchedSubString(searchTerm: string, string?: string): string | undefined {
    if (typeof string === "undefined" || string.length === 0) {
        return undefined;
    }

    const match = string.match(RegExp(escapeRegExpCharacters(searchTerm), "gi"));

    if (match === null || !Array.isArray(match)) {
        return undefined;
    }

    return match[0];
}

/**
 * Removes given `char` from the start and the end of the given `string`
 * @param char
 * @param string
 */
function trimChar(char: string, string: string) {
    const trimmed = string.trim();

    if (trimmed.length === 0) {
        return trimmed;
    }

    let result = trimmed;

    for (let i = 0; i < trimmed.length; i++) {
        if (result.startsWith(char)) {
            result = result.slice(1);
        }

        if (result.endsWith(char)) {
            result = result.slice(0, -1);
        }

        if (!result.startsWith(char) && !result.endsWith(char)) {
            break;
        }
    }

    return result;
}

/**
 * Specific formatting for given domain `ext` string
 * @param ext
 * @example
 * ```
 * formatTopLevelDomainExtension("..co.uk....") -> ".co.uk"
 * formatTopLevelDomainExtension("co.uk.") -> ".co.uk"
 * formatTopLevelDomainExtension("com") -> ".com"
 * ```
 */
export function formatTopLevelDomainExtension(ext: string) {
    return `.${trimChar(".", ext)}`;
}
